import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/Modal';
import WorkerSelect from '../components/WorkerSelect';
import { PaymentModal } from './PaymentsPage';

function fmt(n) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 2 }).format(n || 0);
}

const WORK_TYPES = ['Hasat','Budama','Sulama','Ekim/Dikim','İlaçlama','Çapalama','Gübreleme','Paketleme','Diğer'];

const EMPTY = {
  workerName: '', groupName: '', workType: 'Hasat',
  numWorkers: 1, dailyRate: '', days: 1,
  paymentStatus: 'pending', workDate: new Date().toISOString().split('T')[0], notes: '',
};

export default function LabourPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [payTarget, setPayTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/farm/labour').then(r => setItems(r.data.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const calcTotal = () => (parseFloat(form.dailyRate) || 0) * (parseFloat(form.days) || 1) * (parseInt(form.numWorkers) || 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/farm/labour', form);
      toast.success('İşçilik kaydedildi ✓');
      setShowModal(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error('Kaydedilemedi');
    } finally { setSaving(false); }
  };

  const total = items.reduce((s, i) => s + parseFloat(i.total_amount || 0), 0);
  const pending = items.filter(i => i.payment_status === 'pending').reduce((s, i) => s + parseFloat(i.total_amount || 0), 0);

  return (
    <div>
      <div className="card-row" style={{ marginBottom: 12 }}>
        <h2 className="section-title" style={{ margin: 0 }}>İşçilik</h2>
        <span style={{ fontWeight: 800, color: '#e74c3c', fontSize: '1.05rem' }}>{fmt(total)}</span>
      </div>

      {/* Legacy view — the full registry lives in Yevmiyeler */}
      <div onClick={() => navigate('/yevmiyeler')}
        style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--green-pale)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '10px 12px', marginBottom: 12, cursor: 'pointer' }}>
        <span style={{ fontSize: '1.2rem' }}>👷</span>
        <span style={{ flex: 1, fontSize: '0.82rem', color: '#2d5a27', fontWeight: 600 }}>
          Tüm işçilik kayıtlarını (hasat, ekim, iş emri dahil) görmek için Yevmiyeler bölümünü kullanın
        </span>
        <span style={{ color: 'var(--green-mid)', fontWeight: 800 }}>→</span>
      </div>

      {pending > 0 && (
        <div className="card" style={{ background: '#fef9e7', border: '1px solid #f0c040', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '1.3rem' }}>⚠️</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>Bekleyen ödemeler</div>
              <div style={{ color: '#e67e22', fontWeight: 800 }}>{fmt(pending)}</div>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        {loading ? <div className="empty-state"><p>Yükleniyor...</p></div>
          : items.length === 0 ? (
            <div className="empty-state"><div className="icon">👷</div><p>Henüz işçilik kaydı yok</p></div>
          ) : items.map(item => (
            <div key={item.id} className="txn-item">
              <div className="txn-main">
                <div className="txn-name">{item.worker_name}</div>
                <div className="txn-meta">
                  {new Date(item.work_date).toLocaleDateString('tr-TR')} · {item.work_type}
                  {item.num_workers > 1 && ` · ${item.num_workers} kişi`}
                  {` · ${item.days} gün`}
                </div>
                <span className={`badge badge-${item.payment_status}`}>
                  {item.payment_status === 'paid' ? 'Ödendi' : 'Bekliyor'}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                <span className="txn-amount expense">{fmt(item.total_amount)}</span>
                {item.payment_status !== 'paid' && (
                  <button onClick={() => setPayTarget(item)}
                    style={{ background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 8,
                      padding: '3px 8px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}>
                    💳 Öde
                  </button>
                )}
              </div>
            </div>
          ))}
      </div>

      <button className="btn-fab" onClick={() => setShowModal(true)}>+</button>

      {payTarget && (
        <PaymentModal
          entityType="labour" entityId={payTarget.id}
          entityName={`${payTarget.worker_name} · ${payTarget.work_type}`} totalAmount={payTarget.total_amount}
          direction="out" onClose={() => setPayTarget(null)} onSaved={load}
        />
      )}

      {showModal && (
        <Modal title="Yeni İşçilik Ekle" onClose={() => setShowModal(false)}>
          <form onSubmit={handleSubmit}>
            <WorkerSelect
              label="İşçi / Grup Adı"
              value={form.workerName}
              onChange={v => set('workerName', v)}
              onWorkerSelect={w => { set('workerName', `${w.first_name} ${w.last_name}`); set('dailyRate', w.default_daily_rate || ''); }}
              placeholder="İsim veya grup adı"
              required
            />
            <div className="form-group">
              <label>İş Türü</label>
              <select value={form.workType} onChange={e => set('workType', e.target.value)}>
                {WORK_TYPES.map(w => <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Kişi Sayısı</label>
                <input type="number" min="1" value={form.numWorkers} onChange={e => set('numWorkers', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Gün Sayısı</label>
                <input type="number" step="0.5" min="0.5" value={form.days} onChange={e => set('days', e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Günlük Ücret (₺)</label>
                <input type="number" step="0.01" value={form.dailyRate}
                  onChange={e => set('dailyRate', e.target.value)} placeholder="0.00" />
              </div>
              <div className="form-group">
                <label>Toplam</label>
                <input readOnly value={calcTotal() > 0 ? `₺${calcTotal().toFixed(2)}` : ''}
                  style={{ background: '#f0f7ee', fontWeight: 700 }} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Tarih</label>
                <input type="date" value={form.workDate} onChange={e => set('workDate', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Ödeme</label>
                <select value={form.paymentStatus} onChange={e => set('paymentStatus', e.target.value)}>
                  <option value="paid">✅ Ödendi</option>
                  <option value="pending">⏳ Bekliyor</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button type="button" className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }}
                onClick={() => setShowModal(false)}>İptal</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} disabled={saving}>
                {saving ? 'Kaydediliyor...' : '💾 Kaydet'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
